import { NOYAU_COLOR_TOKEN, NOYAU_STEP_ORDER } from '@mss_creator/shared';
import type { Noyau, NoyauColorToken } from '@mss_creator/shared';
import type { CSSProperties } from 'react';
import { palierGradient } from './noyau-gradient';
import { cn } from './utils';

/**
 * The lookup tables behind the visual display tweaks, in one place. The real
 * screens (matrix grid, singularity setup editor, accent scope) and the
 * settings-page miniatures both read from here, so a miniature can never
 * drift from the screen it imitates. Class names are written out literally
 * because Tailwind only sees class names that appear in the source.
 */

// matrix-cell-padding

/** Padding classes for the `matrix-cell-padding` tweak, applied to body and header cells alike. */
export const CELL_PADDING_CLASS: Readonly<Record<string, string>> = {
  loose: 'px-4 py-3',
  compact: 'px-3 py-2',
  tight: 'px-2 py-1',
};

const DEFAULT_CELL_PADDING = 'compact';

/** Padding classes for one tweak value; an unknown value falls back to `compact`. */
export function cellPaddingClassFor(value: string | undefined): string {
  return CELL_PADDING_CLASS[value ?? DEFAULT_CELL_PADDING] ?? CELL_PADDING_CLASS.compact ?? '';
}

// matrix-header-theme

/**
 * Fixed palier to header-theme token mapping for the `pitch` theme. Paliers are
 * always this same 8-step sequence, so this is a hardcoded lookup, not
 * per-company data.
 *
 * Palier -10 is neutral grey (issue #16, planche 06): the same `gray-200` the
 * `noyau-gradient` theme gives it, so both themes read sabotage as off-scale. Its
 * ring is a darker grey, because a `gray-200` ring would vanish on the selected header.
 */
export const PALIER_HEADER_THEME: Readonly<Record<number, { bg: string; ring: string }>> = {
  [-10]: { bg: 'bg-gray-200', ring: 'ring-gray-500' }, // pms-design-exception: en-tête du palier -10 : gris de la charte (spec de la grille)
  0: { bg: 'bg-palier-attentes-0', ring: 'ring-palier-attentes-0' },
  1: { bg: 'bg-palier-attentes-1', ring: 'ring-palier-attentes-1' },
  2: { bg: 'bg-palier-attentes-2', ring: 'ring-palier-attentes-2' },
  3: { bg: 'bg-palier-attentes-3', ring: 'ring-palier-attentes-3' },
  4: { bg: 'bg-palier-singularite-4', ring: 'ring-palier-singularite-4' },
  5: { bg: 'bg-palier-singularite-5', ring: 'ring-palier-singularite-5' },
  6: { bg: 'bg-palier-singularite-6', ring: 'ring-palier-singularite-6' },
};

/**
 * Background class of one palier header. The `noyau-gradient` theme paints via
 * inline style (`palierGradient`), so it contributes no class.
 */
export function headerBackgroundClassFor(theme: string, palierNumber: number): string {
  if (theme === 'noyau-gradient') return '';
  if (theme !== 'pitch') return 'bg-white';
  return PALIER_HEADER_THEME[palierNumber]?.bg ?? 'bg-white';
}

/**
 * Inline background / text colour for one palier header. Text colour is set on
 * every theme (not just the gradient) so the palier label inherits it. Only the
 * gradient theme adds a background, since it is a `color-mix()` expression.
 */
export function headerStyleFor(theme: string, noyau: Noyau, palierNumber: number): CSSProperties {
  if (theme !== 'noyau-gradient') return { color: 'var(--color-noir)' };
  const gradient = palierGradient(noyau, palierNumber);
  return {
    background: gradient.background,
    color: gradient.color,
    '--palier-ring': gradient.ring,
  } as CSSProperties;
}

/** Selection ring class of one palier header, per theme. */
export function headerSelectionRingClassFor(theme: string, palierNumber: number): string {
  if (theme === 'pitch') return PALIER_HEADER_THEME[palierNumber]?.ring ?? 'ring-bleu-nuit';
  if (theme === 'noyau-gradient') return 'ring-[var(--palier-ring)]';
  return 'ring-bleu-nuit';
}

// matrix-activation-levier-colors / singularity-step-decoration

/** Left border colour per noyau colour token (activation header, setup step). */
export const LEVIER_BORDER_CLASSES: Readonly<Record<NoyauColorToken, string>> = {
  'bleu-nuit': 'border-l-bleu-nuit',
  'bleu-ciel': 'border-l-bleu-ciel',
  vert: 'border-l-vert',
  jaune: 'border-l-jaune',
  rouge: 'border-l-rouge',
  rose: 'border-l-rose',
};

/** Tinted background per noyau colour token. */
export const LEVIER_TINT_CLASSES: Readonly<Record<NoyauColorToken, string>> = {
  'bleu-nuit': 'bg-bleu-nuit-tint',
  'bleu-ciel': 'bg-bleu-ciel-tint',
  vert: 'bg-vert-tint',
  jaune: 'bg-jaune-tint',
  rouge: 'bg-rouge-tint',
  rose: 'bg-rose-tint',
};

/** Solid background per noyau colour token (dots, side band segments). */
export const LEVIER_SOLID_CLASSES: Readonly<Record<NoyauColorToken, string>> = {
  'bleu-nuit': 'bg-bleu-nuit',
  'bleu-ciel': 'bg-bleu-ciel',
  vert: 'bg-vert',
  jaune: 'bg-jaune',
  rouge: 'bg-rouge',
  rose: 'bg-rose',
};

/**
 * The colour token of the levier / setup step at `order` (1-6), via the
 * company noyau step order. The same derivation serves the activation rows of
 * the matrix and the rows of the setup table, so the two always agree.
 */
export function levierColorTokenFor(noyau: Noyau, order: number): NoyauColorToken {
  const stepNoyau = NOYAU_STEP_ORDER[noyau][order - 1] ?? noyau;
  return NOYAU_COLOR_TOKEN[stepNoyau];
}

/** What the activation block actually paints, after the names fallback. */
export type LevierColorTreatment = 'off' | 'accent' | 'tint' | 'bar';

/**
 * Resolves the `matrix-activation-levier-colors` value against
 * `matrix-activation-levier-names`. With names hidden there is no per-levier
 * header cell to put a border or tint on, so `accent` and `tint` degrade to the
 * six-segment band rather than appearing to do nothing.
 */
export function resolveLevierColorTreatment(colors: string, names: string): LevierColorTreatment {
  if (colors === 'off') return 'off';
  if (names === 'hidden' && colors !== 'bar') return 'bar';
  return colors as LevierColorTreatment;
}

/** The two paints of a `singularity-step-decoration` value. */
export function stepDecorationParts(decoration: string): { accent: boolean; tint: boolean } {
  return {
    accent: decoration === 'accent' || decoration === 'both',
    tint: decoration === 'tint' || decoration === 'both',
  };
}

/** Classes of one setup step row, split by where they go (see `stepDecorationClasses`). */
export interface StepDecorationClasses {
  /** Every cell of the row: separator on top, plus the tint when asked. */
  cell: string;
  /** The first cell only: the left border, when asked. */
  firstCell: string;
  /** The coloured dot next to the step label, or `null` when there is none. */
  dot: string | null;
}

/**
 * Classes for one step row of the setup table under a `singularity-step-decoration`
 * value. The borders sit on cells, never on the `<tr>`: a row of a
 * `border-separate` table paints no border of its own (H10).
 */
export function stepDecorationClasses(
  decoration: string,
  token: NoyauColorToken,
): StepDecorationClasses {
  const { accent, tint } = stepDecorationParts(decoration);
  return {
    cell: cn('border-gray-100 border-t-2', tint && LEVIER_TINT_CLASSES[token]),
    firstCell: accent ? cn('border-l-4', LEVIER_BORDER_CLASSES[token]) : '',
    dot: accent ? LEVIER_SOLID_CLASSES[token] : null,
  };
}

// matrix-noyaux-accent

const NOYAU_ACCENT_VARS: Readonly<
  Record<NoyauColorToken, { base: string; tint: string; deep: string }>
> = {
  'bleu-nuit': {
    base: 'var(--color-bleu-nuit)',
    tint: 'var(--color-bleu-nuit-tint)',
    deep: 'var(--color-bleu-nuit-deep)',
  },
  'bleu-ciel': {
    base: 'var(--color-bleu-ciel)',
    tint: 'var(--color-bleu-ciel-tint)',
    deep: 'var(--color-bleu-ciel-deep)',
  },
  vert: {
    base: 'var(--color-vert)',
    tint: 'var(--color-vert-tint)',
    deep: 'var(--color-vert-deep)',
  },
  jaune: {
    base: 'var(--color-jaune)',
    tint: 'var(--color-jaune-tint)',
    deep: 'var(--color-jaune-deep)',
  },
  rouge: {
    base: 'var(--color-rouge)',
    tint: 'var(--color-rouge-tint)',
    deep: 'var(--color-rouge-deep)',
  },
  rose: {
    base: 'var(--color-rose)',
    tint: 'var(--color-rose-tint)',
    deep: 'var(--color-rose-deep)',
  },
};

/**
 * Custom-property override that makes every `bg-bleu-nuit` / `text-bleu-nuit` /
 * `border-bleu-nuit` utility of a subtree pick up the colour of `noyau`.
 * `undefined` for `bleu-nuit` itself (no override needed).
 */
export function noyauAccentStyle(noyau: NoyauColorToken): CSSProperties | undefined {
  if (noyau === 'bleu-nuit') return undefined;
  const vars = NOYAU_ACCENT_VARS[noyau];
  return {
    '--color-bleu-nuit': vars.base,
    '--color-bleu-nuit-tint': vars.tint,
    '--color-bleu-nuit-deep': vars.deep,
  } as CSSProperties;
}

/** A tweak value as a noyau colour token; anything unknown reads as `bleu-nuit`. */
export function asNoyauColorToken(value: string): NoyauColorToken {
  return Object.hasOwn(NOYAU_ACCENT_VARS, value) ? (value as NoyauColorToken) : 'bleu-nuit';
}
